import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/context/AuthContext";
import {
  getAllTemplates,
  addTemplateSection,
  updateTemplateSection,
  deleteTemplateSection,
} from "@/lib/api";
import {
  FileText, Search, Plus, Edit, Trash2,
  AlertTriangle, CheckCircle, Loader2, BookOpen,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { TemplateBuilderModal } from "@/components/TemplateBuilderModal";

// ─── Types ────────────────────────────────────────────────────────────────────

type TemplateType = "complaint" | "medicine" | "investigation" | "advice";
type TemplateCategory = "personal" | "clinic";

interface Template {
  id: string;
  title: string;
  description?: string;
  type: TemplateType;
  value: any;
  category: TemplateCategory;
  usage_count: number;
  is_favorite: 0 | 1;
  is_active: 0 | 1;
  created_at: string;
  updated_at: string;
}

// ─── Config ───────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES: Record<TemplateType, { label: string }> = {
  complaint:     { label: "Complaints" },
  medicine:      { label: "Medicines" },
  investigation: { label: "Investigations" },
  advice:        { label: "Advice" },
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function TemplateManagementPage() {
  const { currentUser } = useAuth();

  useEffect(() => { document.title = "Templates | OneCheckUp"; }, []);

  const [templates,    setTemplates]    = useState<Template[]>([]);
  const [activeTab,    setActiveTab]    = useState("all");
  const [searchTerm,   setSearchTerm]   = useState("");
  const [loading,      setLoading]      = useState(false);
  const [notification, setNotification] = useState<{ type: "success" | "error"; message: string } | null>(null);
  const [builderOpen,    setBuilderOpen]    = useState(false);
  const [builderInitial, setBuilderInitial] = useState<any>(null);
  const [builderLoading, setBuilderLoading] = useState(false);

  // ── Data ────────────────────────────────────────────────────────────────────

  const fetchTemplates = useCallback(async () => {
    if (!currentUser?.doctor_id || !currentUser?.clinic_id) return;
    try {
      setLoading(true);
      const data = await getAllTemplates(currentUser.doctor_id, currentUser.clinic_id);
      setTemplates((data as any)?.templates || []);
    } catch {
      showNotification("error", "Failed to load templates");
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.doctor_id, currentUser?.clinic_id]);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  // ── Notifications ───────────────────────────────────────────────────────────

  const showNotification = (type: "success" | "error", message: string) => {
    setNotification({ type, message });
  };

  useEffect(() => {
    if (!notification) return;
    const t = setTimeout(() => setNotification(null), 3000);
    return () => clearTimeout(t);
  }, [notification]);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const filteredTemplates = useMemo(() => {
    return templates.filter(t => {
      if (activeTab !== "all" && t.type !== activeTab) return false;
      if (searchTerm) {
        const q = searchTerm.toLowerCase();
        return t.title.toLowerCase().includes(q) || t.description?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [templates, activeTab, searchTerm]);

  const getTypeCount = (type: string) =>
      type === "all" ? templates.length : templates.filter(t => t.type === type).length;

  // ── Builder open/close ──────────────────────────────────────────────────────

  const openCreateModal = () => {
    setBuilderInitial({ title: "", description: "", type: "medicine", category: "personal", value: [] });
    setBuilderOpen(true);
  };

  const openEditModal = (template: Template) => {
    // Normalize value before passing to builder
    let value = template.value;

    if (typeof value === "string" && ["medicine", "complaint", "investigation"].includes(template.type)) {
      try { value = JSON.parse(value); } catch { value = []; }
    }

    if (["advice", "diagnosis", "quick_notes"].includes(template.type)) {
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          value = typeof parsed?.value === "string" ? parsed.value : value;
        } catch { /* keep as string */ }
      } else if (value && typeof value === "object") {
        value = typeof value.value === "string" ? value.value : "";
      } else {
        value = "";
      }
    }

    setBuilderInitial({ ...template, value });
    setBuilderOpen(true);
  };

  const closeBuilder = () => {
    setBuilderOpen(false);
    setBuilderInitial(null);
  };

  // ── CRUD ────────────────────────────────────────────────────────────────────

  const handleBuilderSave = async (data: any) => {
    setBuilderLoading(true);
    try {
      if (builderInitial?.id) {
        await updateTemplateSection(builderInitial.id, {
          ...data,
          doctor_id: currentUser?.doctor_id,
          clinic_id: currentUser?.clinic_id,
        });
        showNotification("success", "Template updated");
      } else {
        await addTemplateSection({
          ...data,
          doctor_id: currentUser?.doctor_id,
          clinic_id: currentUser?.clinic_id,
        });
        showNotification("success", "Template created");
      }
      closeBuilder();
      fetchTemplates();
    } catch {
      showNotification("error", "Failed to save template");
    } finally {
      setBuilderLoading(false);
    }
  };

  const handleDelete = async (templateId: string, templateTitle: string) => {
    if (!currentUser?.doctor_id) return;
    if (!confirm(`Delete "${templateTitle}"?`)) return;
    try {
      setLoading(true);
      await deleteTemplateSection(templateId, currentUser.doctor_id);
      showNotification("success", "Template deleted");
      fetchTemplates();
    } catch {
      showNotification("error", "Failed to delete template");
    } finally {
      setLoading(false);
    }
  };



  // ── Render ──────────────────────────────────────────────────────────────────

  return (
      <div className="prescription-form-container">

        {/* Header */}
        <div className="border-b border-border pb-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-primary/10 rounded-xl">
                <BookOpen className="w-6 h-6 text-primary" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-foreground">Templates</h1>
                <p className="text-sm text-muted-foreground">
                  {templates.length} template{templates.length !== 1 ? "s" : ""}
                </p>
              </div>
            </div>
            <Button onClick={openCreateModal} disabled={loading}>
              <Plus className="w-4 h-4 mr-2" />
              New Template
            </Button>
          </div>
        </div>

        {/* Notification */}
        {notification && (
            <Alert className={cn(
                "mb-6 border-l-4",
                notification.type === "success"
                    ? "border-success bg-success/10"
                    : "border-destructive bg-destructive/10"
            )}>
              {notification.type === "success"
                  ? <CheckCircle className="h-4 w-4 text-success" />
                  : <AlertTriangle className="h-4 w-4 text-destructive" />
              }
              <AlertDescription className={notification.type === "success" ? "text-success" : "text-destructive"}>
                {notification.message}
              </AlertDescription>
            </Alert>
        )}

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
                placeholder="Search templates…"
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-10"
            />
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="all">All ({getTypeCount("all")})</TabsTrigger>
            {Object.entries(TEMPLATE_TYPES).map(([type, config]) => (
                <TabsTrigger key={type} value={type}>
                  {config.label} ({getTypeCount(type)})
                </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value={activeTab}>
            {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
            ) : filteredTemplates.length === 0 ? (
                <Card>
                  <CardContent className="p-12 text-center">
                    <FileText className="w-12 h-12 text-muted-foreground/40 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-1">No templates found</h3>
                    <p className="text-sm text-muted-foreground mb-6">
                      {searchTerm ? "No templates match your search." : "Create your first template to get started."}
                    </p>
                    {!searchTerm && (
                        <Button onClick={openCreateModal}>
                          <Plus className="w-4 h-4 mr-2" />
                          Create Template
                        </Button>
                    )}
                  </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTemplates.map(template => (
                      <Card key={template.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between mb-2">
                            <div className="min-w-0">
                              <h3 className="font-medium text-sm truncate">{template.title}</h3>
                              <div className="flex items-center gap-1.5 mt-1">
                                <Badge variant="outline" className="text-xs">
                                  {TEMPLATE_TYPES[template.type]?.label}
                                </Badge>
                                {template.category === "clinic" && (
                                    <Badge variant="secondary" className="text-xs">Clinic</Badge>
                                )}
                              </div>
                            </div>
                          </div>

                          {template.description && (
                              <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                                {template.description}
                              </p>
                          )}

                          <div className="flex gap-1 mt-3">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openEditModal(template)}
                                className="flex-1"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleDelete(template.id, template.title)}
                                className="text-destructive hover:text-destructive"
                                title="Delete"
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                  ))}
                </div>
            )}
          </TabsContent>
        </Tabs>

        <TemplateBuilderModal
            open={builderOpen}
            onClose={closeBuilder}
            onSave={handleBuilderSave}
            initialData={builderInitial || {}}
            loading={builderLoading}
        />

      </div>
  );
}